import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TestimonialsSection.module.css';
import { OptimizedMotionH2 } from '../../../shared/ui/optimized-motion/OptimizedMotion';
import { AnimatePresence, motion } from 'framer-motion';
import StartButton from '../shared/start-button/StartButton';
import IconArrowRight from '../../../shared/ui/icons/IconArrowRight';
import IconArrowLeft from '../../../shared/ui/icons/IconArrowLeft';
const TestimonialsSection: React.FC = () => {
    const navigate = useNavigate();
    const [activeSlide, setActiveSlide] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDotClick = (index: number) => {
        setActiveSlide(index);
    };

    // Swipe handlers
    const handleSwipe = (direction: 'left' | 'right') => {
        if (direction === 'left') {
            nextSlide();
        } else {
            prevSlide();
        }
    };

    const testimonials = [
        {
            name: 'Donna Sloan',
            role: 'Director - GLYTCH Energy',
            text: "As someone juggling creative projects and client work, I often know what I want to say but struggle with finding a starting point. SimpleeCreate gave me that initial spark for copy and visuals, and let me edit all the content it generated and shape it to match my tone and vision. I appreciated how specific I could get with image prompts, and how easy it was to fine-tune the generated images to be more authentic. It is a great blend of inspiration and control, and I would prescribe it for anyone struggling with writer's block.",
            rating: '5.0',
        },
        {
            name: 'xAvengedxWolfx',
            role: 'Content Creator - kick.com/twitch.tv',
            text: 'SimpleeCreate AI is truly a game-changer! Super easy to use! Fast and helps bring ideas to life with just a few clicks. Whether you are creating content, branding, or just experimenting, it is powerful and beginner-friendly. Highly recommend for creators of all levels! Even from the farm stables I was able to create a Twitter post that captured all of my needs before going live!',
            rating: '5.0',
        },
        {
            name: 'ChillGiver',
            role: 'Content Creator - twitch.tv',
            text: 'The directions for use were straightforward and easy to understand. The options given to select from for topics and what kind of content are useful if you are not sure where to get started. I felt like I could easily make a prompt for whatever idea I was trying to bring to life, and the posts and images that Simplee Create created for me all fit in my style of posts and all required little to no changes before posting. It is most definitely simpler to create with Simplee Create.',
            rating: '5.0',
        },
        {
            name: 'NineBanqzz',
            role: 'Content Creator - twitch.tv/youtube.com',
            text: 'Before I started using Simplee Create I always had writers block. I never knew what to post or how to post it. With Simplee Create, that problem was over in a matter of seconds, literally! It saves me so much time, and even motivates me to post daily stuff. It is fast, easy, and fun to use. You have a small idea and they make it a reality in a matter of seconds. As a streamer / content creator, these seconds to minutes can save you so much time for all the other stuff, so I highly recommend this program to anyone posting stuff on social media!',
            rating: '5.0',
        },
    ];

    const testimonialsPerPage = isMobile ? 1 : 2;
    const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

    const currentTestimonials = testimonials.slice(
        activeSlide * testimonialsPerPage,
        (activeSlide + 1) * testimonialsPerPage,
    );

    const nextSlide = () => {
        if (activeSlide < totalPages - 1) {
            setActiveSlide(activeSlide + 1);
        } else {
            setActiveSlide(0);
        }
    };

    const prevSlide = () => {
        if (activeSlide > 0) {
            setActiveSlide(activeSlide - 1);
        } else {
            setActiveSlide(totalPages - 1);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.headerSection}>
                <OptimizedMotionH2 className={styles.mainTitle} type="slide-down" delay={0.3}>
                    <span className={styles.titleHighlight}>
                        What our clients
                        <br />{' '}
                    </span>{' '}
                    really think about us
                </OptimizedMotionH2>

                {/* Navigation arrows */}
                <div className={styles.navigationArrows}>
                    <motion.button
                        className={styles.navButton}
                        onClick={prevSlide}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <IconArrowLeft />
                    </motion.button>
                    <motion.button
                        className={styles.navButton}
                        onClick={nextSlide}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <IconArrowRight />
                    </motion.button>
                </div>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={activeSlide}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.6 }}
                    className={styles.testimonialsRow}
                    drag={isMobile ? 'x' : false}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.2}
                    onDragEnd={(e, { offset, velocity }) => {
                        const swipeThreshold = 50;
                        if (offset.x > swipeThreshold) {
                            handleSwipe('right');
                        } else if (offset.x < -swipeThreshold) {
                            handleSwipe('left');
                        }
                    }}
                >
                    {currentTestimonials.map((testimonial, index) => (
                        <div key={testimonial.name} className={styles.testimonialCard}>
                            <div className={styles.testimonialContent}>
                                <p className={styles.testimonialText}>{testimonial.text}</p>

                                <div className={styles.testimonialFooter}>
                                    <div className={styles.clientInfo}>
                                        <h3 className={styles.clientName}>{testimonial.name}</h3>
                                        <p className={styles.clientRole}>{testimonial.role}</p>
                                    </div>

                                    <div className={styles.starsRow}>
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className={styles.star}>
                                                ⭐
                                            </div>
                                        ))}
                                    </div>
                                    <span className={styles.ratingScore}>{testimonial.rating}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </motion.div>
            </AnimatePresence>

            {/* Mobile Navigation arrows */}
            <div className={styles.mobileNavigationArrows}>
                <motion.button
                    className={styles.navButton}
                    onClick={prevSlide}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Previous testimonial"
                >
                    <IconArrowLeft />
                </motion.button>
                <motion.button
                    className={styles.navButton}
                    onClick={nextSlide}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Next testimonial"
                >
                    <IconArrowRight />
                </motion.button>
            </div>
        </div>
    );
};

export default TestimonialsSection;
